import { apiClient } from '@/lib/api-client';
import type {
  BackupDestination,
  BackupOverview,
  BackupRecord,
  BackupSchedule,
  BackupScope,
  BackupSource,
  BackupVerification,
} from '@/types/api';

export const BACKUPS_KEY = ['backups'] as const;

export interface DestinationInput {
  name: string;
  kind: 'local' | 's3';
  location: string;
  endpoint?: string;
  region?: string;
  access_key_id?: string;
  secret_key?: string;
}

/**
 * What a source protects: a directory, a database, or both, on one server.
 *
 * An empty `directory` means no files, and an empty `database_engine` means no database; the API
 * refuses a source with neither.
 */
export interface SourceInput {
  server_id: string;
  name: string;
  directory: string;
  database_engine: '' | 'mysql' | 'postgresql';
  database_name: string;
}

export const backupsApi = {
  /** Sources, destinations, schedules, recent backups and their verification, in one request. */
  overview: () => apiClient.get<BackupOverview>('/backups'),

  addSource: (input: SourceInput) => apiClient.post<BackupSource>('/backups/sources', input),

  /**
   * Corrects where a source's data is.
   *
   * The server can only change while the source has no backups; after that a restore onto the new
   * server is what moves it, and the API says so.
   */
  updateSource: (id: string, input: SourceInput) => apiClient.put<BackupSource>(`/backups/sources/${id}`, input),

  /** Forgets a source and its schedule. Backups already taken, and the data on the server, stay. */
  removeSource: (id: string) => apiClient.delete<void>(`/backups/sources/${id}`),

  addDestination: (input: DestinationInput) =>
    apiClient.post<BackupDestination>('/backups/destinations', input),

  removeDestination: (id: string) => apiClient.delete<void>(`/backups/destinations/${id}`),

  setSchedule: (input: {
    source_id: string;
    destination_id: string;
    frequency: 'daily' | 'weekly';
    retention_count: number;
    scope: BackupScope;
  }) => apiClient.put<BackupSchedule>('/backups/schedules', input),

  setScheduleEnabled: (id: string, enabled: boolean) =>
    apiClient.patch<void>(`/backups/schedules/${id}`, { enabled }),

  removeSchedule: (id: string) => apiClient.delete<void>(`/backups/schedules/${id}`),

  /**
   * Takes a backup now, synchronously.
   *
   * A long request by design — the customer clicked a button and is waiting for the answer. The
   * response always carries a finished record, succeeded or failed with a reason.
   */
  runNow: (sourceId: string, destinationId: string, scope: BackupScope = 'full') =>
    apiClient.post<BackupRecord>('/backups/run', {
      source_id: sourceId,
      destination_id: destinationId,
      scope,
    }),

  /**
   * Restores over the live data on the source's server.
   *
   * `confirmation` must be the source's own name. The server enforces it, not just the UI: this is
   * the one operation that destroys the customer's current state.
   *
   * `targetServerId` restores onto a different server and moves the source there. Omitted for the
   * ordinary case, which puts the backup back where it came from.
   */
  restore: (id: string, confirmation: string, targetServerId?: string) =>
    apiClient.post<{ restored: boolean; log: string[] }>(`/backups/${id}/restore`, {
      confirmation,
      ...(targetServerId ? { target_server_id: targetServerId } : {}),
    }),

  /**
   * Asks where this backup can be downloaded from.
   *
   * Two possible answers. A backup in the customer's own cloud storage comes back as a presigned
   * link straight to their bucket, so the browser fetches it directly and the platform never sees a
   * byte. One kept on their server comes back as a signed link to the platform, which pipes it
   * through — that one is capped, because it holds a worker for the whole transfer.
   *
   * Either way the caller just navigates to `url`; the difference matters for cost, not for code.
   */
  downloadLink: (id: string, format: 'tar' | 'zip') =>
    apiClient.post<{ direct: boolean; url: string; filename: string }>(
      `/backups/${id}/download?format=${format}`,
      {},
    ),

  removeBackup: (id: string) => apiClient.delete<void>(`/backups/${id}`),

  /**
   * Proves one backup restores, without touching the live data.
   *
   * Answers at once with the queued test. The worker fetches the archive, checks it byte for byte
   * and loads its database into a scratch copy that is dropped afterwards; the overview picks up the
   * result, and the page polls while a test is running.
   */
  testRestore: (id: string) => apiClient.post<BackupVerification>(`/backups/${id}/test-restore`, {}),
};
